import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import HomePage from "@/pages/HomePage";
import "@/App.css";

// Route-level code-splitting: HomePage stays in the main bundle so first
// paint is instant; everything else loads on demand.
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const AssistancePage = lazy(() => import("@/pages/AssistancePage"));
const PropertyDetailPage = lazy(() => import("@/pages/PropertyDetailPage"));
const PostRequirementPage = lazy(() => import("@/pages/PostRequirementPage"));
const AddPage = lazy(() => import("@/pages/AddPage"));
const AddPropertyPage = lazy(() => import("@/pages/AddPropertyPage"));
const AddRequirementPage = lazy(() => import("@/pages/AddRequirementPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const PublicProfilePage = lazy(() => import("@/pages/PublicProfilePage"));
const SavedPage = lazy(() => import("@/pages/SavedPage"));
const AuthGate = lazy(() => import("@/components/AuthGate"));

function Protected({ children, adminOnly = false }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === undefined) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

const RouteSpinner = () => (
  <div className="min-h-[40vh] grid place-items-center text-muted-foreground text-sm">Loading…</div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Layout>
          <Suspense fallback={<RouteSpinner />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/assistance" element={<AssistancePage />} />
              <Route path="/properties/:id" element={<PropertyDetailPage />} />
              <Route path="/u/:uid" element={<PublicProfilePage />} />
              <Route path="/saved" element={<SavedPage />} />
              <Route path="/post-requirement" element={<PostRequirementPage />} />
              <Route path="/add" element={<Protected><AddPage /></Protected>} />
              <Route path="/add/property" element={<AuthGate variant="postProperty"><AddPropertyPage /></AuthGate>} />
              <Route path="/properties/:id/edit" element={<Protected><AddPropertyPage /></Protected>} />
              <Route path="/add/requirement" element={<Protected><AddRequirementPage /></Protected>} />
              <Route path="/requirements/:id/edit" element={<Protected><AddRequirementPage /></Protected>} />
              <Route path="/dashboard" element={<AuthGate variant="dashboard"><DashboardPage /></AuthGate>} />
              <Route path="/admin" element={<Protected adminOnly><AdminPage /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </Layout>
        <Toaster position="top-center" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
